import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./WelcomeScreen.css";
import { useSelector } from "react-redux";

const WelcomeScreen = ({ onComplete }) => {
  const [showContent, setShowContent] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const loadingMessages = [
    "Loading GitHub stats...",
    "Loading LeetCode stats...",
    "Fetching repositories...",
    "Analyzing contributions...",
    "Preparing portfolio...",
  ];

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setShowLoading(true);
    }, 4000);

    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    if (showLoading && progress < 100) {
      const progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressTimer);
            return 100;
          }
          return prev + 1;
        });
      }, 30);

      const messageTimer = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 1000);

      return () => {
        clearInterval(progressTimer);
        clearInterval(messageTimer);
      };
    }
  }, [showLoading]);

  const { loading } = useSelector(
    (state) => state.techProfileGetUserPublicDataStore
  );

  useEffect(() => {
    if (progress === 100 && !loading) {
      const exitTimer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onComplete?.();
        }, 1000);
      }, 500);

      return () => clearTimeout(exitTimer);
    }
  }, [progress, onComplete, loading]);

  const calc = (x, speed = 1) => x * speed;

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          className="welcome-container"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 0.8,
            transition: {
              duration: 0.8,
              ease: "easeInOut",
            },
          }}
        >
          {/* Glowing Circles Background */}
          <div className="welcome-glow glow-1"></div>
          <div className="welcome-glow glow-2"></div>
          <div className="welcome-glow glow-3"></div>
          <div className="welcome-glow glow-4"></div>

          {/* Scanline Effect */}
          <div className="scanline"></div>

          {/* Animated Circuit Lines */}
          <motion.div className="tech-background">
            <div className="circuit-line circuit-line-1"></div>
            <div className="circuit-line circuit-line-2"></div>
            <div className="circuit-line circuit-line-3"></div>
            <div className="circuit-line circuit-line-4"></div>

            {/* Corner Accent Elements */}
            <div className="corner-accent top-left"></div>
            <div className="corner-accent top-right"></div>
            <div className="corner-accent bottom-left"></div>
            <div className="corner-accent bottom-right"></div>
          </motion.div>

          {/* Welcome Text Animation */}
          {showContent && (
            <motion.div
              className="welcome-text-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                y: -50,
                transition: { duration: 0.6 },
              }}
              transition={{ duration: 0.5 }}
            >
              <motion.svg
                className="welcome-svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 800 200"
                fill="none"
                stroke="currentColor"
                strokeWidth="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>Welcome</title>

                {/* W */}
                <motion.path
                  d="M 30 40 L 50 160 L 80 80 L 110 160 L 130 40"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(0.8),
                    ease: "easeInOut",
                    opacity: { duration: 0.4 },
                  }}
                />

                {/* e */}
                <motion.path
                  d="M 180 100 Q 180 70 210 70 Q 240 70 240 100 Q 240 130 210 130 Q 180 130 180 110 L 240 110"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(1),
                    ease: "easeInOut",
                    delay: calc(0.3),
                    opacity: { duration: 0.5, delay: calc(0.3) },
                  }}
                />

                {/* l */}
                <motion.path
                  d="M 270 30 L 270 130"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(0.6),
                    ease: "easeInOut",
                    delay: calc(0.8),
                    opacity: { duration: 0.3, delay: calc(0.8) },
                  }}
                />

                {/* c */}
                <motion.path
                  d="M 340 70 Q 310 70 310 100 Q 310 130 340 130"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(0.7),
                    ease: "easeInOut",
                    delay: calc(1.2),
                    opacity: { duration: 0.35, delay: calc(1.2) },
                  }}
                />

                {/* o */}
                <motion.path
                  d="M 390 70 Q 420 70 420 100 Q 420 130 390 130 Q 360 130 360 100 Q 360 70 390 70"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(1),
                    ease: "easeInOut",
                    delay: calc(1.6),
                    opacity: { duration: 0.5, delay: calc(1.6) },
                  }}
                />

                {/* m */}
                <motion.path
                  d="M 450 130 L 450 70 Q 450 70 470 70 Q 490 70 490 90 L 490 130 M 490 90 Q 490 70 510 70 Q 530 70 530 90 L 530 130"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(1.2),
                    ease: "easeInOut",
                    delay: calc(2.2),
                    opacity: { duration: 0.6, delay: calc(2.2) },
                  }}
                />

                {/* e */}
                <motion.path
                  d="M 580 100 Q 580 70 610 70 Q 640 70 640 100 Q 640 130 610 130 Q 580 130 580 110 L 640 110"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: calc(1),
                    ease: "easeInOut",
                    delay: calc(2.8),
                    opacity: { duration: 0.5, delay: calc(2.8) },
                  }}
                />

                {/* Decorative dot */}
                <motion.circle
                  cx="700"
                  cy="110"
                  r="6"
                  fill="#9900ffff"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: calc(3.6),
                    ease: "backOut",
                  }}
                />
              </motion.svg>
            </motion.div>
          )}

          {/* Loading Progress Section */}
          {showLoading && (
            <motion.div
              className="loading-container"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: 50,
                transition: { duration: 0.5 },
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {/* Loading Messages */}
              <div className="loading-messages">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={messageIndex}
                    className="loading-message"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{
                      duration: 0.5,
                      ease: "easeInOut",
                    }}
                  >
                    {loadingMessages[messageIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* Progress Bar Container */}
              <div className="progress-bar-container">
                <motion.div
                  className="progress-bar"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: progress / 100 }}
                  transition={{ duration: 0.1, ease: "linear" }}
                />
                <div className="progress-glow"></div>
              </div>

              {/* Progress Percentage */}
              <motion.div
                className="progress-percentage"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {progress}%
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeScreen;
